// Pure, side-effect-free helpers for the PWA invite flow. Kept separate from admin-client.js so the
// gating and payload logic can be unit-tested without a network or Supabase session.
//
// The real authorization is always enforced server-side: request_user_invitation and
// recover_user_invitation re-check the user_invite permission against the database on every call.
// These helpers only drive the UI (what to show, what to send) -- they never grant access on their own.

// The permission that gates invitation. Only administrator and owner_approver bundles hold it
// (see migration 20260721170000_owner_approver_user_invite.sql).
export const INVITE_PERMISSION = "user_invite";

// Role bundles an inviter may assign. Owners and administrators may target any active bundle,
// including administrator (product decision 2026-07-21). Order = display order in the form.
export const ROLE_OPTIONS = Object.freeze([
  { code: "executor", label: "Uitvoerder" },
  { code: "owner_approver", label: "Owner (goedkeurder)" },
  { code: "administrator", label: "Administrator" },
]);

const VALID_ROLE_CODES = new Set(ROLE_OPTIONS.map((role) => role.code));

// A pragmatic email shape check -- the server/Supabase Auth is the real validator; this only gives
// the user immediate feedback before a round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Find the access-context entry for a project, or null if the actor has no membership there.
 * Accepts arbitrary input and validates defensively, since it runs on a raw API response.
 * @param {*} context result of current_access_context()
 * @param {string} projectId
 */
export function findProjectContext(context, projectId) {
  const projects = context?.projects;
  if (!Array.isArray(projects)) return null;
  return projects.find((project) => project?.project_id === projectId) ?? null;
}

/**
 * Whether the current actor may invite users into the given project.
 * @param {*} context result of current_access_context()
 * @param {string} projectId
 * @returns {boolean}
 */
export function canInvite(context, projectId) {
  const project = findProjectContext(context, projectId);
  const permissions = project?.permissions;
  return Array.isArray(permissions) && permissions.includes(INVITE_PERMISSION);
}

function normalizedEmail(value) {
  return String(value ?? "").trim().toLowerCase();
}

function validateEmail(email) {
  if (!email) return "Vul een e-mailadres in.";
  if (!EMAIL_PATTERN.test(email)) return "Vul een geldig e-mailadres in.";
  return null;
}

/**
 * Validate the form input and build the POST body for dbvdw-admin /invitations, or return an error
 * message (in Dutch, for direct display) when the input is not usable.
 *
 * @param {{ email?: string, displayName?: string, roleBundleCode?: string,
 *           project?: { project_id?: string, organization_id?: string } }} input
 * @returns {{ payload: {
 *   email: string,
 *   display_name: string,
 *   organization_id: string,
 *   project_id: string,
 *   role_bundle_code: string
 * } } | { error: string }}
 */
export function buildInvitationPayload(input) {
  const email = normalizedEmail(input?.email);
  const displayName = String(input?.displayName ?? "").trim();
  const roleBundleCode = String(input?.roleBundleCode ?? "").trim();
  const project = input?.project ?? {};
  const emailError = validateEmail(email);

  if (emailError) return { error: emailError };
  if (!displayName) return { error: "Vul een naam in." };
  if (!VALID_ROLE_CODES.has(roleBundleCode)) return { error: "Kies een geldige rol." };
  if (!project.project_id || !project.organization_id) {
    return { error: "Geen geldig project geselecteerd." };
  }

  return {
    payload: {
      email,
      display_name: displayName,
      organization_id: project.organization_id,
      project_id: project.project_id,
      role_bundle_code: roleBundleCode,
    },
  };
}

/**
 * Validate and shape the narrowly scoped recovery request. The role is intentionally not supplied:
 * recovery must use the role bundle stored on the original invitation.
 *
 * @param {{ email?: string, project?: { project_id?: string } }} input
 * @returns {{ payload: { email: string, project_id: string } } | { error: string }}
 */
export function buildRecoveryPayload(input) {
  const email = normalizedEmail(input?.email);
  const projectId = String(input?.project?.project_id ?? "").trim();
  const emailError = validateEmail(email);

  if (emailError) return { error: emailError };
  if (!projectId) return { error: "Geen geldig project geselecteerd." };

  return { payload: { email, project_id: projectId } };
}
